// Premium BookaTherapy HomePage - Redesigned for clarity and premium feel
"use client";
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import {
  MapPin,
  Home as HomeIcon,
  Smartphone,
  Building,
  Check,
  Sparkles,
  Heart,
  Clock,
  Shield,
  Award,
  ChevronRight,
  Calendar,
  Mail,
  Phone,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Gift,
  Trophy,
  Users,
  X,
  Star,
  ThumbsUp,
  Zap,
} from "lucide-react";

import { Button, buttonVariants } from "./ui/button";
import { Badge } from "./ui/badge";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "./context/AuthContext";
import logo from "../images/logowhite.svg";
import { NavbarDemo } from "./NavBar";
import { reverseGeocodePostal, lookupPostalDetails } from "../lib/location";
import SEOHead from "./SEOHead";
import TrackedSection from "./TrackedSection";
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
  generateFAQSchema,
} from "../utils/seo";

export default function NewHomePage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [userLocation, setUserLocation] = useState(null);
  const [locationDisplay, setLocationDisplay] = useState(
    "📍 Click to set location",
  );
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();

  // Load AnyChat widget on the homepage
  useEffect(() => {
    const widgetId = "de1b58b1-a6ce-3cb0-ae5b-29f7c003d055";
    const scriptId = "contactus-jssdk";

    if (document.getElementById(scriptId)) return;

    const fjs = document.getElementsByTagName("script")[0];
    const js = document.createElement("script");
    js.id = scriptId;
    js.src = `https://api.anychat.one/widget/${widgetId}/livechat-js?r=${encodeURIComponent(
      window.location,
    )}`;
    fjs.parentNode.insertBefore(js, fjs);
  }, []);

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  useEffect(() => {
    const stored = localStorage.getItem("bookatherapy_location");
    if (stored) {
      try {
        const loc = JSON.parse(stored);
        setUserLocation(loc);
        setLocationDisplay(`📍 ${loc.postalCode}, ${loc.city}`);
        return;
      } catch (error) {
        console.error("Error parsing stored location:", error);
        localStorage.removeItem("bookatherapy_location");
      }
    }

    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser");
      return;
    }

    let cancelled = false;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        if (cancelled) return;
        const { latitude, longitude } = position.coords;
        console.log("Location received:", { latitude, longitude });

        try {
          const locationData = await reverseGeocodePostal(latitude, longitude);
          console.log("Reverse geocode result:", locationData);

          if (!cancelled && locationData?.postalCode) {
            const postalDetails = await lookupPostalDetails(
              locationData.postalCode,
            );
            console.log("Postal details:", postalDetails);

            if (postalDetails) {
              const loc = {
                ...locationData,
                ...postalDetails,
              };
              setUserLocation(loc);
              setLocationDisplay(`📍 ${loc.postalCode}, ${loc.city}`);
              localStorage.setItem("bookatherapy_location", JSON.stringify(loc));

              toast({
                title: "Location set",
                description: `Using ${loc.postalCode}, ${loc.city}`,
              });
            }
          } else {
            console.warn("No postal code found in location data");
            toast({
              title: "Location Error",
              description: "Could not determine your postal code. Please enter it manually.",
              variant: "destructive",
            });
          }
        } catch (err) {
          console.error("Geocoding error:", err);
          toast({
            title: "Location Error",
            description: err.message || "Failed to get your location. Please enter postal code manually.",
            variant: "destructive",
          });
        }
      },
      (error) => {
        console.error("Geolocation permission error:", error);
        let errorMessage = "Location access denied.";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied. Please enable location access in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable. Please enter your postal code manually.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again or enter postal code manually.";
            break;
          default:
            errorMessage = "Unable to get location. Please enter your postal code manually.";
        }

        toast({
          title: "Location Access",
          description: errorMessage,
          variant: "destructive",
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );

    return () => {
      cancelled = true;
    };
  }, [toast]);

  const handleLocationClick = () => {
    const postal = prompt(
      "Enter your postal code (e.g. M5V 3A8):",
      userLocation?.postalCode || "",
    );
    if (!postal) return;
    const cleaned = postal.trim().toUpperCase().replace(/\s+/g, " ");
    lookupPostalDetails(cleaned)
      .then((details) => {
        if (details) {
          const loc = { postalCode: cleaned, ...details };
          setUserLocation(loc);
          setLocationDisplay(`📍 ${loc.postalCode}, ${loc.city}`);
          localStorage.setItem("bookatherapy_location", JSON.stringify(loc));
          toast({
            title: "Location Updated",
            description: `Set to ${loc.city}, ${loc.province || loc.postalCode}`,
          });
        } else {
          toast({
            title: "Invalid Postal Code",
            description: "Please enter a valid Canadian postal code.",
            variant: "destructive",
          });
        }
      })
      .catch((error) => {
        console.error("Postal code lookup error:", error);
        const isApiKeyError = error.message?.includes("API key");
        toast({
          title: isApiKeyError ? "Configuration Error" : "Lookup Failed",
          description: isApiKeyError
            ? "Location service is not configured. Please enter your location manually when booking."
            : "Could not lookup postal code. You can still enter it during booking.",
          variant: "destructive",
        });
      });
  };

  const organizationSchema = generateOrganizationSchema();
  const websiteSchema = generateWebsiteSchema();
  const faqSchema = generateFAQSchema();

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEOHead
        title="BookaTherapy - Premium Massage Therapy Booking Platform"
        description="Find and book professional massage therapists instantly. Choose from Swedish, Deep Tissue, Sports Massage, and more. Book at home, mobile, or clinic. Transparent pricing and verified therapists."
        keywords="massage therapy, book massage, massage therapist, spa services, deep tissue massage, swedish massage, sports massage, prenatal massage, home massage, mobile massage"
        canonicalUrl="https://bookatherapy.com"
        ogType="website"
        ogImage="https://bookatherapy.com/og-image.jpg"
        structuredData={[organizationSchema, websiteSchema, faqSchema]}
      />

      <NavbarDemo />

      {/* Premium Hero Section */}
      <TrackedSection sectionName="hero_section" sectionId="hero">
      <section className="relative overflow-hidden pt-20 sm:pt-24 md:pt-28 lg:pt-32 pb-20 sm:pb-28 md:pb-36 lg:pb-40">
        {/* Massage background image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600334129128-685c5582fd35?q=80&w=2070&auto=format&fit=crop"
            alt="Relaxing massage therapy"
            className="w-full h-full object-cover"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-800/80 to-slate-900/85"></div>
        </div>

        {/* Premium background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#98C454]/50 to-transparent"></div>
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#98C454]/30 rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-[#98C454]/30 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-96 h-96 bg-[#98C454]/20 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center max-w-5xl mx-auto">

            {/* Premium badge */}
            <motion.div variants={staggerItem} className="mb-8">
              <Badge className="bg-[#98C454]/10 text-[#98C454] border-[#98C454]/30 backdrop-blur-sm px-6 py-2.5 text-sm font-semibold shadow-lg shadow-[#98C454]/10">
                <Sparkles className="w-4 h-4 mr-2" />
                Canada's Premier Massage Therapy Platform
              </Badge>
            </motion.div>

            {/* Hero headline - Clear value proposition */}
            <motion.h1
              variants={staggerItem}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 sm:mb-8 leading-[1.1] tracking-tight px-4">
              <span className="block">Professional Massage</span>
              <span className="block text-[#98C454]">
                Delivered Your Way
              </span>
            </motion.h1>

            {/* Clear subtitle explaining what BookaTherapy does */}
            <motion.p
              variants={staggerItem}
              className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-12 sm:mb-16 max-w-3xl mx-auto leading-relaxed font-light px-4">
              Book verified massage therapists instantly. Choose <span className="text-[#98C454] font-semibold">home</span>, <span className="text-[#98C454] font-semibold">mobile</span>, or <span className="text-[#98C454] font-semibold">clinic</span>.
              Transparent pricing. Professional service. Complete wellness.
            </motion.p>

            {/* Location - Minimal & Premium Centered */}
            <motion.div
              variants={staggerItem}
              className="w-full flex justify-center mb-10 px-4">
              <button
                onClick={handleLocationClick}
                className="group flex items-center justify-center gap-2.5 px-6 py-3 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-[#98C454]/30 transition-all duration-300">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-[#98C454] flex-shrink-0" />
                <span className="text-sm sm:text-base text-gray-300 leading-tight">
                  Showing results near{" "}
                  <span className="font-semibold text-white">
                    {userLocation?.city && userLocation?.postalCode
                      ? `${userLocation.city} (${userLocation.postalCode})`
                      : locationDisplay.replace("📍 ", "")}
                  </span>
                </span>
                <span className="hidden sm:inline text-gray-400">•</span>
                <span className="text-sm sm:text-base text-[#98C454] font-medium group-hover:underline underline-offset-4">
                  Change
                </span>
              </button>
            </motion.div>

            {/* Main CTA Button */}
            <motion.div
              variants={staggerItem}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 sm:mb-16 px-4">
              <Button
                size="lg"
                onClick={() => navigate("/book", {
                  state: { location: userLocation }
                })}
                className="w-full sm:w-auto bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-8 sm:px-12 py-6 sm:py-7 text-lg sm:text-xl rounded-2xl shadow-2xl hover:shadow-[#98C454]/30 transition-all duration-300 font-bold group">
                <Calendar className="mr-3 h-6 w-6" />
                Book Your Session Now
                <ChevronRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              variants={staggerItem}
              className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-400 px-4">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500 fill-yellow-500" />
                <span className="whitespace-nowrap">4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-[#98C454]" />
                <span className="whitespace-nowrap">Verified Therapists</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-[#98C454]" />
                <span className="whitespace-nowrap">Same-Day Booking</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path
              d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>
      </TrackedSection>

      {/* How BookaTherapy Works - Clear, Simple */}
      <TrackedSection sectionName="how_it_works_section" sectionId="how-it-works">
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <Badge className="mb-4 bg-[#98C454]/10 text-[#98C454] border-[#98C454]/30 px-4 py-1.5">
              <Zap className="w-4 h-4 mr-2" />
              Simple Process
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#252F4F] mb-3 sm:mb-4 px-4">
              Wellness Made Simple
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Professional massage therapy in three easy steps
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12 max-w-5xl mx-auto">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0, duration: 0.5 }}
              className="relative">
              <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 text-6xl sm:text-7xl font-bold text-gray-100">
                01
              </div>
              <div className="relative bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:border-[#98C454] group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-[#98C454] to-[#7aa840] flex items-center justify-center mb-5 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <Heart className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#252F4F] mb-2 sm:mb-3">
                  Choose Your Service
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Select from Swedish, Deep Tissue, Sports, Prenatal, and more. Browse verified therapists with real reviews.
                </p>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="relative">
              <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 text-6xl sm:text-7xl font-bold text-gray-100">
                02
              </div>
              <div className="relative bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:border-[#98C454] group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-[#98C454] to-[#7aa840] flex items-center justify-center mb-5 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <MapPin className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#252F4F] mb-2 sm:mb-3">
                  Pick Your Location
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Book at home for convenience, mobile for flexibility, or visit a professional clinic. Your comfort, your choice, your way.
                </p>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="relative">
              <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 text-6xl sm:text-7xl font-bold text-gray-100">
                03
              </div>
              <div className="relative bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:border-[#98C454] group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-[#98C454] to-[#7aa840] flex items-center justify-center mb-5 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <Calendar className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#252F4F] mb-2 sm:mb-3">
                  Relax & Enjoy
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Instant booking confirmation. Transparent pricing. Professional therapists. Complete relaxation guaranteed.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      </TrackedSection>

      {/* Service Options - Premium Design */}
      <TrackedSection sectionName="service_options_section" sectionId="service-options">
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <Badge className="mb-4 bg-[#98C454]/10 text-[#98C454] border-[#98C454]/30 px-4 py-1.5">
              <Heart className="w-4 h-4 mr-2" />
              Flexible Options
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#252F4F] mb-3 sm:mb-4 px-4">
              Your Comfort, Your Choice
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Professional massage therapy delivered exactly how you want it
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: HomeIcon,
                title: "At Your Home",
                description: "Therapists bring everything needed. You enjoy massage in the comfort of your own space.",
                features: ["Professional setup", "All equipment included", "Maximum comfort"],
                gradient: "from-[#98C454] to-[#7aa840]",
              },
              {
                icon: Smartphone,
                title: "Mobile Service",
                description: "Perfect for busy schedules. Professional therapists come to your office or hotel.",
                features: ["Ultimate flexibility", "No travel time", "Book anywhere"],
                gradient: "from-[#98C454] to-[#7aa840]",
                featured: true,
              },
              {
                icon: Building,
                title: "Visit a Clinic",
                description: "Traditional spa experience. Professional facilities with premium amenities.",
                features: ["Spa atmosphere", "Premium facilities", "Classic experience"],
                gradient: "from-[#98C454] to-[#7aa840]",
              },
            ].map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`relative bg-white rounded-3xl p-6 sm:p-8 ${
                  option.featured
                    ? "border-2 border-[#98C454] shadow-2xl shadow-[#98C454]/20 sm:scale-105"
                    : "border border-gray-200 shadow-xl hover:shadow-2xl"
                } transition-all duration-300 hover:scale-105`}>

                {option.featured && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-[#98C454] to-[#7aa840] text-white border-0 px-4 py-1.5 shadow-lg text-xs sm:text-sm">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}

                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${option.gradient} flex items-center justify-center mb-5 sm:mb-6 shadow-lg`}>
                  <option.icon className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">
                  {option.title}
                </h3>
                <p className="text-sm sm:text-base text-slate-600 mb-5 sm:mb-6 leading-relaxed">
                  {option.description}
                </p>

                <ul className="space-y-2 sm:space-y-3 mb-5 sm:mb-6">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm sm:text-base text-slate-700">
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500 mr-2 sm:mr-3 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      </TrackedSection>

      {/* Why Choose BookaTherapy - Premium Trust Signals */}
      <TrackedSection sectionName="why_choose_section" sectionId="why-choose">
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200 px-4 py-1.5">
              <Award className="w-4 h-4 mr-2" />
              Premium Standards
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-4">
              Why Canadians Trust BookaTherapy
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto px-4">
              Setting the standard for professional massage therapy booking
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 md:gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: Shield,
                title: "Verified Professionals",
                description: "All therapists are licensed, insured, and background-checked",
              },
              {
                icon: Clock,
                title: "Instant Booking",
                description: "Book same-day appointments with instant confirmation",
              },
              {
                icon: ThumbsUp,
                title: "Transparent Pricing",
                description: "No hidden fees. Clear pricing before you book",
              },
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                className="text-center group">
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-5 sm:mb-6 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <benefit.icon className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3 px-2">
                  {benefit.title}
                </h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed px-2">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      </TrackedSection>

      {/* Gift Cards Promotion Section */}
      <TrackedSection sectionName="gift_cards_section" sectionId="gift-cards">
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-[#98C454]/10 via-white to-[#98C454]/5 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 right-10 w-64 h-64 sm:w-96 sm:h-96 bg-[#98C454]/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 sm:w-96 sm:h-96 bg-[#252F4F]/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>
              <Badge className="mb-5 sm:mb-6 bg-[#98C454] text-[#252F4F] border-[#98C454] px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold shadow-lg">
                <Gift className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                Perfect for Any Occasion
              </Badge>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#252F4F] mb-5 sm:mb-6">
                Give the Gift of{" "}
                <span className="text-[#98C454]">Wellness</span>
              </h2>

              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Treat someone special to a relaxing massage experience. Our gift
                cards are the perfect way to show you care.
              </p>

              {/* Benefits List */}
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#98C454] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-[#252F4F] mb-0.5 sm:mb-1">
                      Digital Delivery
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600">
                      Instant email delivery or schedule for a special date
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#98C454] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-[#252F4F] mb-0.5 sm:mb-1">
                      Flexible Amounts
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600">
                      Choose any amount from $50 to $500
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#98C454] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-[#252F4F] mb-0.5 sm:mb-1">
                      Never Expires
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600">
                      No expiration date - use anytime
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#98C454] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-[#252F4F] mb-0.5 sm:mb-1">
                      Personal Touch
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600">
                      Add a custom message and design
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate("/gift-cards")}
                  className="w-full sm:w-auto bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold">
                  <Gift className="mr-2 h-5 w-5" />
                  Buy a Gift Card
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  onClick={() => navigate("/gift-cards/redeem")}
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-[#252F4F] text-[#252F4F] hover:bg-[#252F4F] hover:text-white px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg rounded-xl font-semibold transition-all duration-300">
                  Redeem a Gift Card
                </Button>
              </div>
            </motion.div>

            {/* Right: Gift Card Preview */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative mt-8 lg:mt-0">
              {/* Gift Card Mockup */}
              <div className="relative">
                {/* Main Card */}
                <div className="bg-gradient-to-br from-[#252F4F] via-[#1e2640] to-[#252F4F] rounded-3xl p-8 sm:p-10 md:p-12 shadow-2xl transform hover:scale-105 transition-transform duration-500 relative overflow-hidden">
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-48 h-48 sm:w-64 sm:h-64 bg-[#98C454]/20 rounded-full -mr-24 sm:-mr-32 -mt-24 sm:-mt-32"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 sm:w-48 sm:h-48 bg-[#98C454]/10 rounded-full -ml-16 sm:-ml-24 -mb-16 sm:-mb-24"></div>

                  <div className="relative z-10">
                    <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
                      <Gift className="h-10 w-10 sm:h-12 sm:w-12 text-[#98C454]" />
                      <div>
                        <h3 className="text-xl sm:text-2xl font-bold text-white">
                          BookaTherapy
                        </h3>
                        <p className="text-[#98C454] text-xs sm:text-sm">Gift Card</p>
                      </div>
                    </div>

                    <div className="mb-6 sm:mb-8">
                      <p className="text-gray-400 text-xs sm:text-sm mb-1 sm:mb-2">
                        Gift Card Value
                      </p>
                      <p className="text-4xl sm:text-5xl font-bold text-white">$100</p>
                    </div>

                    <div className="bg-white/10 rounded-xl p-3 sm:p-4 mb-5 sm:mb-6 backdrop-blur-sm">
                      <p className="text-gray-300 text-xs sm:text-sm mb-1">Code</p>
                      <p className="text-white font-mono text-base sm:text-lg tracking-wider">
                        GIFT-XXXX-XXXX
                      </p>
                    </div>

                    <p className="text-gray-400 text-xs">
                      Valid for all massage services • Never expires
                    </p>
                  </div>
                </div>

                {/* Floating Badge */}
                <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 bg-[#98C454] text-[#252F4F] px-4 py-2 sm:px-6 sm:py-3 rounded-full shadow-xl font-bold text-sm sm:text-lg transform rotate-12">
                  Perfect Gift!
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-6 sm:mt-8">
                <div className="text-center bg-white rounded-2xl p-3 sm:p-4 shadow-lg">
                  <div className="text-2xl sm:text-3xl font-bold text-[#252F4F]">5K+</div>
                  <div className="text-xs sm:text-sm text-gray-600">Cards Sold</div>
                </div>
                <div className="text-center bg-white rounded-2xl p-3 sm:p-4 shadow-lg">
                  <div className="text-2xl sm:text-3xl font-bold text-[#98C454]">4.9★</div>
                  <div className="text-xs sm:text-sm text-gray-600">Rating</div>
                </div>
                <div className="text-center bg-white rounded-2xl p-3 sm:p-4 shadow-lg">
                  <div className="text-2xl sm:text-3xl font-bold text-[#252F4F]">100%</div>
                  <div className="text-xs sm:text-sm text-gray-600">Satisfaction</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Clinic Owners Partnership Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

            {/* Left side - Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=2053&auto=format&fit=crop"
                  alt="Modern massage therapy clinic"
                  className="w-full h-[400px] sm:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#252F4F]/60 via-transparent to-transparent"></div>

                {/* Floating stats card */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-xl">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-[#252F4F]">500+</div>
                      <div className="text-xs sm:text-sm text-gray-600">Clinics</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-[#98C454]">50K+</div>
                      <div className="text-xs sm:text-sm text-gray-600">Bookings</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-[#252F4F]">98%</div>
                      <div className="text-xs sm:text-sm text-gray-600">Fill Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right side - Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2">

              <Badge className="mb-6 bg-[#98C454]/10 text-[#98C454] border-[#98C454]/30 px-6 py-2 text-sm font-semibold">
                <Building className="w-4 h-4 mr-2" />
                For Clinic Owners
              </Badge>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#252F4F] mb-6 leading-tight">
                Grow Your Clinic with BookaTherapy
              </h2>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Join Canada's premier massage therapy platform and connect with thousands of clients actively seeking professional massage services. Streamline your bookings, reduce no-shows, and grow your business.
              </p>

              {/* Benefits list */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#98C454] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#252F4F] mb-1">Instant Client Access</h3>
                    <p className="text-gray-600">Reach thousands of clients searching for massage therapy near you</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#98C454] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#252F4F] mb-1">Smart Booking Management</h3>
                    <p className="text-gray-600">Automated scheduling, reminders, and payment processing</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#98C454] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#252F4F] mb-1">Grow Your Revenue</h3>
                    <p className="text-gray-600">Fill empty slots and maximize your clinic's earning potential</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => window.open('https://clinic.bookatherapy.com', '_blank')}
                  className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-8 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold group">
                  <Building className="mr-2 h-5 w-5" />
                  Join as a Clinic Partner
                  <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => window.open('https://clinic.bookatherapy.com', '_blank')}
                  className="border-2 border-[#252F4F] text-[#252F4F] hover:bg-[#252F4F] hover:text-white px-8 py-6 text-lg rounded-xl font-semibold transition-all duration-300">
                  Learn More
                </Button>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
      </TrackedSection>

      {/* Final CTA Section - Premium */}
      <TrackedSection sectionName="final_cta_section" sectionId="final-cta">
      <section className="relative py-20 sm:py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-emerald-900 to-teal-900"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 sm:mb-6 leading-tight px-4">
              Experience the Difference
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-10 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-4">
              Join thousands of Canadians who've discovered a better way to book massage therapy
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/book")}
              className="w-full sm:w-auto bg-white hover:bg-slate-100 text-slate-900 px-8 sm:px-10 md:px-12 py-6 sm:py-7 text-base sm:text-lg rounded-2xl shadow-2xl hover:shadow-white/20 transition-all duration-300 font-semibold group">
              <Calendar className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6" />
              Start Your Wellness Journey
              <ChevronRight className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>
      </TrackedSection>

      {/* Footer */}
      <footer className="bg-[#252F4F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* Company Info */}
            <div className="sm:col-span-2 lg:col-span-1">
              <img src={logo} alt="BookaTherapy" className="h-10 sm:h-12 mb-3 sm:mb-4" />
              <p className="text-sm sm:text-base text-gray-300 mb-3 sm:mb-4 leading-relaxed">
                Your trusted platform for personalized massage therapy.
                Connecting you with certified RMTs for ultimate wellness.
              </p>
              <div className="flex gap-3 sm:gap-4">
                <a
                  href="https://www.facebook.com/share/1FochVcQ5T/?mibextid=wwXIfr"
                  className="w-10 h-10 bg-white/10 hover:bg-[#98C454] rounded-lg flex items-center justify-center transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
                <a
                  href="https://www.instagram.com/booka_therapy"
                  className="w-10 h-10 bg-white/10 hover:bg-[#98C454] rounded-lg flex items-center justify-center transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="https://x.com/booka_therapy"
                  className="w-10 h-10 bg-white/10 hover:bg-[#98C454] rounded-lg flex items-center justify-center transition-colors">
                  <X className="h-5 w-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/bookatherapy"
                  className="w-10 h-10 bg-white/10 hover:bg-[#98C454] rounded-lg flex items-center justify-center transition-colors">
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-[#98C454]">
                Quick Links
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="/book"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors">
                    Book a Session
                  </a>
                </li>
                <li>
                  <a
                    href="/therapath"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors">
                    Find Therapists
                  </a>
                </li>
                <li>
                  <a
                    href="/auth"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors">
                    Sign In / Sign Up
                  </a>
                </li>
                <li>
                  {user ? (
                    <Link
                    to="/dashboard"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors">
                    Client Dashboard
                    </Link>
                    ) : null}
                </li>
              </ul>
            </div>

            {/* Portal Links */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-[#98C454]">
                Professional Portals
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="https://clinic.bookatherapy.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors flex items-center gap-2">
                    <Building className="h-3 w-3 sm:h-4 sm:w-4" />
                    Clinic Portal
                  </a>
                </li>
                <li>
                  <a
                    href="https://practitioner.bookatherapy.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors flex items-center gap-2">
                    <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                    Practitioner Portal
                  </a>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-[#98C454]">
                Our Services
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="/services"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors cursor-pointer">
                    Deep Tissue Massage
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors cursor-pointer">
                    Swedish Massage
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors cursor-pointer">
                    Sports Massage
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors cursor-pointer">
                    Hot Stone Therapy
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors cursor-pointer">
                    Prenatal Massage
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors cursor-pointer">
                    Aromatherapy Massage
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-[#98C454]">
                Contact Us
              </h3>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start gap-2 sm:gap-3">
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-[#98C454] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Email</p>
                    <a
                      href="mailto:info@bookatherapy.com"
                      className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors break-all">
                      info@bookatherapy.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-[#98C454] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Phone</p>
                    <a
                      href="tel:+17802677551"
                      className="text-sm sm:text-base text-gray-300 hover:text-[#98C454] transition-colors">
                      +1 (780) 267-7551
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-[#98C454] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Location</p>
                    <p className="text-sm sm:text-base text-gray-300">Serving all of Alberta</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-6 sm:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
              <p className="text-gray-400 text-xs sm:text-sm text-center md:text-left">
                © {new Date().getFullYear()} BookaTherapy. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
                <Link
                  to="/privacy-policy"
                  className="text-gray-400 hover:text-[#98C454] transition-colors">
                  Privacy Policy
                </Link>
                <Link
                  to="/terms-of-service"
                  className="text-gray-400 hover:text-[#98C454] transition-colors">
                  Terms of Service
                </Link>
                <Link
                  to="/cancellation-policy"
                  className="text-gray-400 hover:text-[#98C454] transition-colors">
                  Cancellation Policy
                </Link>
                <Link
                  to="/manage-cookies"
                  className="text-gray-400 hover:text-[#98C454] transition-colors cursor-pointer">
                  Manage Cookies
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
